import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, money } from "@/lib/api";
import confetti from "canvas-confetti";
import { useCart } from "@/context/CartContext";
import { ShareButton } from "@/components/ShareButton";
import { Heart, HandHeart, Gift, PackageOpen, PenLine, Users, Church, Check, ArrowRight, Sparkles, MapPin, Truck, Plus, Search, X } from "lucide-react";

const KIT_INCLUDES = {
  warm: ["17\" Backpack", "50×60 Fleece Blanket", "Warm Ankle Socks", "Knitted Gloves", "Knit Beanie", "Toothbrush + 2 Toothpastes", "Shampoo", "Body Wash", "2 Deodorant Soaps", "Deodorant", "Razor + Shaving Cream", "Comb + Nail File", "Wet Wipes", "Shower Cap", "Resealable Bag"],
  school: ["17\" Classic Backpack", "Pack of 5 Crayons", "Sharpener w/ Dome Cover", "2 Pink Erasers", "Two-Pocket Folder", "Glue Stick", "Highlighter", "70-Page Spiral Notebook", "Pack of 4 Pencils", "Pack of 4 Pens (blue & black)", "12\" Ruler", "Pencil Case"],
};
const KIT_LABEL = { warm: "Warm Essentials Kit", school: "School Supply Backpack Kit" };
const GIA_SEASON_GOAL = 100;
const KIT_TAGLINE = {
  warm: "A dignified, ready-to-give backpack of warmth and hygiene essentials for someone facing hard times.",
  school: "A 17-inch Classic Backpack (assorted colors) with a full 20-piece school supply kit to set a child up to succeed.",
};

export default function GraceInAction() {
  const { items, addItem } = useCart();
  const [kits, setKits] = useState([]);
  const [fulfillment, setFulfillment] = useState("bulk");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [shipTarget, setShipTarget] = useState("");
  const [note, setNote] = useState("");
  const [warmPhoto, setWarmPhoto] = useState(0);
  const [schoolPhoto, setSchoolPhoto] = useState(0);
  const [showcaseImg, setShowcaseImg] = useState("https://images.unsplash.com/photo-1584515933487-779824d29309?w=800");
  const [warmPhotos, setWarmPhotos] = useState([]);
  const [schoolPhotos, setSchoolPhotos] = useState([]);
  const [schoolColors, setSchoolColors] = useState([]);
  const [addons, setAddons] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [kitsSent, setKitsSent] = useState(null);

  const fireCelebration = () => {
    confetti({ particleCount: 90, spread: 72, origin: { y: 0.35 }, scalar: 0.9,
      colors: ["#8E9A86", "#C4A4A4", "#D9CBA0", "#B4894A"], disableForReducedMotion: true });
  };

  useEffect(() => {
    api.get("/impact/kits").then((r) => {
      const n = r.data.kits_sent || 0;
      setKitsSent(n);
      const pct = (n / GIA_SEASON_GOAL) * 100;
      const tier = [100, 75, 50, 25].find((t) => pct >= t) || 0;
      try {
        const prev = parseInt(localStorage.getItem("hf_gia_ms") || "0", 10);
        if (tier > prev) {
          localStorage.setItem("hf_gia_ms", String(tier));
          if (tier >= 25) setTimeout(fireCelebration, 700);
        }
      } catch { /* ignore */ }
    }).catch(() => {});
  }, []);

  const gia_pct = kitsSent && kitsSent > 0 ? (kitsSent / GIA_SEASON_GOAL) * 100 : 0;
  const gia_milestone = gia_pct >= 100 ? "Goal reached — thank you! 🎉"
    : gia_pct >= 75 ? "So close — 75% of the way there! 🎉"
    : gia_pct >= 50 ? "Halfway there! 🎉"
    : gia_pct >= 25 ? "We're 25% of the way — keep it going! 💛" : "";

  useEffect(() => { api.get("/grace-in-action").then((r) => setKits(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/grace-in-action/addons").then((r) => setAddons(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/settings").then((r) => {
    if (r.data.grace_showcase_image) setShowcaseImg(r.data.grace_showcase_image);
    setWarmPhotos(r.data.grace_warm_photos || []);
    setSchoolPhotos(r.data.grace_school_photos || []);
    setSchoolColors(r.data.grace_school_colors || []);
  }).catch(() => {}); }, []);

  const colorFor = (type) => (type === "school" ? (schoolColors[schoolPhoto] || null) : null);
  const priceFor = (k) => (fulfillment === "bulk" ? (k.price_bulk ?? k.price) : k.price);
  const inCart = (k) => { const c = colorFor(k.kit_type || "warm"); return items.some((i) => i.id === k.id && i.delivery_pref === fulfillment && (i.color || null) === c); };
  const addAddon = (a) => { addItem(a, 1); toast.success(`${a.name.replace("Hope in Action · ", "")} added 💛`); };
  const addKit = (k) => {
    if (fulfillment === "ship" && !shipTarget) {
      toast.error("Please choose whether to ship the kit to you or directly to the recipient.");
      return;
    }
    if (fulfillment === "ship" && deliveryAddress.trim().length < 10) {
      toast.error(shipTarget === "me" ? "Please add your mailing address so we can ship your kit." : "Please add the recipient's name & mailing address.");
      return;
    }
    const type = k.kit_type || "warm";
    const color = colorFor(type);
    const cardNote = Number(k.tier) >= 2 ? (note.trim() || null) : null;
    const addr = fulfillment === "ship"
      ? (shipTarget === "me" ? `Ship to buyer (will personally deliver): ${deliveryAddress.trim()}` : `Ship directly to recipient: ${deliveryAddress.trim()}`)
      : null;
    addItem({ ...k, price: priceFor(k) }, 1, null, cardNote, null, fulfillment, addr, color);
    toast.success(`${KIT_LABEL[type]}${color ? ` (${color})` : ""} — ${k.name.replace("Hope in Action · ", "")} added 💛`);
  };
  const scrollToKits = () => document.getElementById("gia-kits")?.scrollIntoView({ behavior: "smooth" });

  const WAYS = [
    { icon: Gift, title: "Sponsor a care package", desc: "Fund a full kit for someone in your community.", action: scrollToKits, cta: "Sponsor now" },
    { icon: PackageOpen, title: "Donate essential items", desc: "Backpacks, blankets, socks, toiletries — drop-offs welcome.", info: true },
    { icon: PenLine, title: "Write an encouragement card", desc: "A few kind words tucked into a kit can change a day.", info: true },
    { icon: Users, title: "Volunteer your time", desc: "Help us assemble and distribute kits with dignity.", to: "/contact", cta: "Get in touch" },
    { icon: Church, title: "Partner with local ministries", desc: "Bring Hope in Action to your church or organization.", to: "/contact", cta: "Partner with us" },
  ];

  const renderKitBlock = (type) => {
    const blockPhotos = type === "warm" ? warmPhotos : schoolPhotos;
    const photoIdx = type === "warm" ? warmPhoto : schoolPhoto;
    const setPhotoIdx = type === "warm" ? setWarmPhoto : setSchoolPhoto;
    const typeKits = kits
      .filter((k) => (k.kit_type || "warm") === type && !(fulfillment === "bulk" && Number(k.tier) === 1))
      .sort((a, b) => Number(a.tier) - Number(b.tier));
    return (
      <div key={type} className="mb-16" data-testid={`gia-block-${type}`}>
        <div className="grid lg:grid-cols-2 gap-10 items-start mb-10">
          {/* Product photos */}
          <div data-testid={`gia-photos-${type}`}>
            <button type="button" onClick={() => blockPhotos[photoIdx] && setLightbox({ src: blockPhotos[photoIdx], name: KIT_LABEL[type] })}
              className="block w-full rounded-2xl overflow-hidden border border-[#D9CBA0]/50 shadow-sm bg-white relative group" aria-label="View larger image" data-testid={`gia-main-photo-${type}`}>
              {blockPhotos[photoIdx] && <img src={blockPhotos[photoIdx]} alt={`Hope in Action ${KIT_LABEL[type]}`} className="w-full h-auto" loading="lazy" />}
              <span className="absolute top-3 right-3 bg-white/85 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"><Search className="h-4 w-4 text-[#3A3632]" /></span>
            </button>
            {blockPhotos.length > 1 && (
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {blockPhotos.map((url, i) => (
                  <button key={i} onClick={() => setPhotoIdx(i)} data-testid={`gia-photo-thumb-${type}-${i}`}
                    className={`rounded-xl overflow-hidden border-2 transition-colors w-20 h-20 bg-white ${photoIdx === i ? "border-[#8E9A86]" : "border-transparent hover:border-[#D9CBA0]"}`}>
                    <img src={url} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <p className="text-center text-xs text-[#8A8175] mt-2">Tap the photo to see a larger view of what's inside.</p>
          </div>
          {/* Contents */}
          <div>
            <span className="gh-overline">20-piece kit</span>
            <h2 className="font-heading text-3xl sm:text-4xl text-[#3A3632] mt-3">The {KIT_LABEL[type]}</h2>
            <p className="text-[#6B655F] mt-3 leading-relaxed">{KIT_TAGLINE[type]}</p>
            <div className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-[#8E9A86] bg-[#8E9A86]/10 rounded-full px-4 py-1.5"><Truck className="h-4 w-4" /> Free shipping on every kit</div>
            {type === "school" && schoolColors.length > 0 && (
              <div className="mt-5" data-testid="gia-color-picker">
                <label htmlFor="gia-color-select" className="block text-sm font-medium text-[#3A3632] mb-1.5">Backpack color</label>
                <select id="gia-color-select" data-testid="gia-color-select" value={schoolPhoto}
                  onChange={(e) => setSchoolPhoto(Number(e.target.value))}
                  className="w-full sm:w-64 border border-[#D1CCC5] rounded-lg px-4 py-2.5 bg-white outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30 text-sm">
                  {schoolColors.map((c, i) => (<option key={i} value={i}>{c}</option>))}
                </select>
                <p className="text-xs text-[#8A8175] mt-1.5">Choose a color — the photo updates to match, and we'll pack that color for you.</p>
              </div>
            )}
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 mt-6">
              {KIT_INCLUDES[type].map((it) => (
                <li key={it} className="flex items-start gap-2 text-sm text-[#3A3632]"><Check className="h-4 w-4 text-[#8E9A86] mt-0.5 shrink-0" /> {it}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className={`grid gap-6 ${typeKits.length <= 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"}`}>
          {typeKits.map((k) => {
            const featured = Number(k.tier) === 3;
            const other = fulfillment === "bulk" ? k.price : (k.price_bulk ?? k.price);
            return (
              <div key={k.id} data-testid={`gia-kit-${k.slug}`}
                className={`rounded-2xl p-8 flex flex-col bg-white relative ${featured ? "border-2 border-[#8E9A86] bg-[#8E9A86]/6" : "border border-[#E5DFD5]"}`}>
                {k.badge && (
                  <span className={`absolute -top-3 left-8 text-[10px] tracking-wide uppercase font-semibold rounded-full px-3 py-1 ${featured ? "bg-[#8E9A86] text-white" : "bg-[#C4A4A4] text-white"}`}>{k.badge}</span>
                )}
                <h3 className="font-heading text-2xl text-[#3A3632]">{k.name.replace("Hope in Action · ", "")}</h3>
                <div className="font-heading text-4xl text-[#8E9A86] mt-3" data-testid={`gia-price-${k.slug}`}>{money(priceFor(k))}</div>
                <div className="text-xs text-[#8A8175] mt-1">{fulfillment === "ship" ? "Shipped free" : "Bulk via partner"} · {fulfillment === "ship" ? "Bulk" : "Shipped"}: {money(other)}</div>
                {k.perk && <div className="inline-flex items-center gap-1.5 mt-3 w-fit text-xs font-medium text-[#8E9A86] bg-[#8E9A86]/10 rounded-full px-3 py-1.5" data-testid={`gia-perk-${k.slug}`}><Sparkles className="h-3 w-3" /> {k.perk}</div>}
                <p className="text-sm text-[#6B655F] mt-3 leading-relaxed flex-1">{k.description}</p>
                <button onClick={() => addKit(k)} disabled={inCart(k)} data-testid={`gia-add-${k.slug}`}
                  className={`mt-6 w-full rounded-full px-5 py-3 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60 ${featured ? "bg-[#8E9A86] text-white hover:bg-[#7A8772]" : "bg-[#C4A4A4] text-white hover:bg-[#B09090]"}`}>
                  {inCart(k) ? <><Check className="h-4 w-4" /> Added</> : <><Heart className="h-4 w-4" /> Add to cart</>}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="grace-in-action-page">
      {/* Hero */}
      <section className="bg-[#F2EFEB]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase font-semibold text-[#8E9A86] bg-white border border-[#D9CBA0]/60 rounded-full px-4 py-1.5">
            <HandHeart className="h-3.5 w-3.5" /> Sharing hope · Meeting needs · Restoring dignity
          </span>
          <img src="https://customer-assets.emergentagent.com/job_hope-boxes/artifacts/2fcbm6k6_GIFT%20-%20HOPE%20IN%20ACTION%20LOGO.png"
            alt="Hope in Action — compassion in our hands, hope in their hearts" className="w-full max-w-4xl mx-auto h-auto rounded-2xl shadow-sm my-6" data-testid="gia-logo" />
          <p className="font-hand text-3xl text-[#C4A4A4]">Be the hands and feet of grace.</p>
          <div className="gh-hairline my-7 max-w-[120px] mx-auto" />
          <p className="text-lg text-[#6B655F] leading-relaxed font-light">
            Through Hope in Action, we provide basic necessities and words of encouragement to individuals experiencing hard times. Together, we can be the reason someone holds on to hope.
          </p>
          <p className="font-hand text-2xl text-[#8E9A86] mt-5">Every item. Every pack. Every person matters.</p>
          {kitsSent !== null && (
            <div className="mt-6 mx-auto max-w-sm bg-white border border-[#8E9A86]/40 rounded-2xl px-5 py-4 shadow-sm" data-testid="gia-impact">
              <div className="flex items-center justify-center gap-2 text-sm text-[#3A3632]">
                <Sparkles className="h-4 w-4 text-[#8E9A86] shrink-0" />
                <span>
                  {kitsSent > 0
                    ? <>Together, <strong className="text-[#8E9A86]">{kitsSent.toLocaleString()}</strong> of {GIA_SEASON_GOAL} Hope Kits sent this season 💛</>
                    : <>Help us reach <strong className="text-[#8E9A86]">{GIA_SEASON_GOAL}</strong> Hope Kits this season 💛</>}
                </span>
              </div>
              <div className="mt-3 h-2.5 rounded-full bg-[#EDE7DC] overflow-hidden">
                <div className="h-full bg-[#8E9A86] rounded-full transition-all duration-700" data-testid="gia-impact-bar"
                  style={{ width: `${Math.max(2, Math.min(100, Math.round((kitsSent / GIA_SEASON_GOAL) * 100)))}%` }} />
              </div>
              {gia_milestone && (
                <p className="text-xs text-[#8E9A86] font-medium mt-2 text-center" data-testid="gia-milestone">{gia_milestone}</p>
              )}
              <div className="mt-3 pt-3 border-t border-[#EDE7DC] flex items-center justify-center gap-2 flex-wrap" data-testid="gia-impact-share">
                <span className="text-[11px] text-[#8A8175]">Rally your friends:</span>
                <ShareButton
                  title="Hope in Action — Hope & Favor"
                  text={kitsSent > 0 ? "I just helped send a Hope Kit with Hope & Favor 💛 Join me in sending comfort to someone facing hard times:" : "Help send Hope Kits to people facing hard times with Hope & Favor 💛"}
                  className="justify-center" />
              </div>
            </div>
          )}
          <button onClick={scrollToKits} data-testid="gia-hero-cta" className="btn-primary mt-8 inline-flex">
            <Heart className="h-4 w-4" /> Share a little hope
          </button>
          <div className="mt-7 flex flex-col items-center gap-2">
            <span className="gh-overline text-[10px]">Spread the word</span>
            <ShareButton title="Hope in Action · Hope & Favor" text="Sponsor a Warm Essentials or School Supply Backpack care kit for someone in need through Hope & Favor's Hope in Action." />
          </div>
        </div>
      </section>

      {/* Kits */}
      <section id="gia-kits" className="max-w-6xl mx-auto px-5 sm:px-8 py-16 scroll-mt-28">
        {/* Shared delivery + note options */}
        <div className="text-center mb-12">
          <span className="gh-overline">Choose your impact</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#3A3632] mt-3">Share hope your way</h2>

          {/* How it's delivered (sets price) */}
          <div className="max-w-2xl mx-auto mt-6 text-left bg-white border border-[#E5DFD5] rounded-2xl p-6" data-testid="gia-fulfillment">
            <div className="flex items-center gap-2 justify-center"><MapPin className="h-4 w-4 text-[#8E9A86]" /><span className="gh-overline">How would you like it delivered?</span></div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <label className={`flex flex-col gap-1 rounded-xl border p-4 cursor-pointer transition-colors ${fulfillment === "ship" ? "border-[#8E9A86] bg-[#8E9A86]/6" : "border-[#E5DFD5]"}`} data-testid="gia-fulfillment-ship">
                <span className="flex items-center gap-2"><input type="radio" name="gia-ful" checked={fulfillment === "ship"} onChange={() => setFulfillment("ship")} className="accent-[#8E9A86]" /><span className="font-medium text-[#3A3632]">Ship to an address</span></span>
                <span className="text-sm text-[#6B655F] pl-6">If the recipient has a place to stay. <span className="text-[#8E9A86] font-medium">Free shipping.</span></span>
              </label>
              <label className={`flex flex-col gap-1 rounded-xl border p-4 cursor-pointer transition-colors ${fulfillment === "bulk" ? "border-[#8E9A86] bg-[#8E9A86]/6" : "border-[#E5DFD5]"}`} data-testid="gia-fulfillment-bulk">
                <span className="flex items-center gap-2"><input type="radio" name="gia-ful" checked={fulfillment === "bulk"} onChange={() => setFulfillment("bulk")} className="accent-[#8E9A86]" /><span className="font-medium text-[#3A3632]">Bulk via a local partner</span></span>
                <span className="text-sm text-[#6B655F] pl-6">Delivered to a local ministry/partner. <span className="text-[#8E9A86] font-medium">Lower cost.</span> Sponsor one kit or many.</span>
              </label>
            </div>
            {fulfillment === "ship" && (
              <div className="mt-4" data-testid="gia-ship-target">
                <div className="text-center"><span className="gh-overline">Who is this gift for?</span></div>
                <div className="mt-3 grid sm:grid-cols-2 gap-3">
                  <label className={`flex flex-col gap-1 rounded-xl border p-4 cursor-pointer transition-colors ${shipTarget === "me" ? "border-[#8E9A86] bg-[#8E9A86]/6" : "border-[#E5DFD5]"}`} data-testid="gia-ship-me">
                    <span className="flex items-center gap-2"><input type="radio" name="gia-ship-target" checked={shipTarget === "me"} onChange={() => setShipTarget("me")} className="accent-[#8E9A86]" /><span className="font-medium text-[#3A3632]">Ship to Me</span></span>
                    <span className="text-sm text-[#6B655F] pl-6">I'll personally deliver this gift to someone in need.</span>
                  </label>
                  <label className={`flex flex-col gap-1 rounded-xl border p-4 cursor-pointer transition-colors ${shipTarget === "recipient" ? "border-[#8E9A86] bg-[#8E9A86]/6" : "border-[#E5DFD5]"}`} data-testid="gia-ship-recipient">
                    <span className="flex items-center gap-2"><input type="radio" name="gia-ship-target" checked={shipTarget === "recipient"} onChange={() => setShipTarget("recipient")} className="accent-[#8E9A86]" /><span className="font-medium text-[#3A3632]">Ship Directly to the Recipient</span></span>
                    <span className="text-sm text-[#6B655F] pl-6">Enter the recipient's mailing address below.</span>
                  </label>
                </div>
                {shipTarget && (
                  <textarea value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} rows={3} data-testid="gia-delivery-address"
                    placeholder={shipTarget === "me" ? "Your name, street address, city, state, ZIP" : "Recipient name, street address, city, state, ZIP"} maxLength={500}
                    className="w-full mt-3 border border-[#E0D9CF] rounded-xl px-4 py-3 bg-white outline-none focus:ring-2 focus:ring-[#8E9A86]/30 resize-y text-sm" />
                )}
              </div>
            )}
            {fulfillment === "bulk" && (
              <p className="text-sm text-[#8A8175] mt-3 leading-relaxed" data-testid="gia-bulk-note">
                <strong className="text-[#8E9A86]">Anyone can sponsor a Hope Kit</strong> — as an individual gift or in bulk. Give just one, or add several to the cart to sponsor many at once; every kit is distributed with dignity through our local ministry partners. Sponsoring starts with the <strong className="text-[#8E9A86]">Sponsor a Hope Kit</strong>, which includes a handwritten encouragement Hope Card tucked inside. Give the gift of hope. Our Essential Kits are created for individuals experiencing hardship or unexpected challenges. Ship the kit to yourself to personally bless someone, or send it directly to the recipient if you have their address.
              </p>
            )}
          </div>

          {kits.some((k) => Number(k.tier) >= 2) && (
            <div className="max-w-2xl mx-auto mt-5 text-left bg-white border border-[#C4A4A4]/40 rounded-2xl p-6" data-testid="gia-note-block">
              <div className="flex items-center gap-2 justify-center"><PenLine className="h-4 w-4 text-[#C4A4A4]" /><span className="gh-overline">Add a personal note <span className="text-[#8A8175] normal-case tracking-normal">(optional)</span></span></div>
              <p className="text-sm text-[#6B655F] mt-2 text-center">We'll <strong>handwrite your words on the encouragement Hope Card</strong> tucked into the Sponsor & Double kits.</p>
              <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={3} data-testid="gia-note-input"
                placeholder="e.g., You are seen, loved, and never alone. 💛"
                className="w-full mt-3 border border-[#E0D9CF] rounded-xl px-4 py-3 bg-[#FBF7F0] outline-none focus:ring-2 focus:ring-[#C4A4A4]/30 resize-y font-hand text-2xl text-[#3A3632] leading-relaxed" />
              <div className="text-right text-[11px] text-[#8A8175] mt-1" data-testid="gia-note-count">{note.length}/300</div>
            </div>
          )}
        </div>

        {/* Both kits, always visible */}
        {renderKitBlock("warm")}
        {renderKitBlock("school")}

        <p className="text-center text-xs text-[#8A8175] mt-1">Sponsored and bulk kits are distributed through our local ministry partners.</p>

        {/* Add extra supplies */}
        {addons.length > 0 && (
          <div className="mt-14" data-testid="gia-addons">
            <div className="text-center mb-8">
              <span className="gh-overline">Add extra care</span>
              <h3 className="font-heading text-2xl sm:text-3xl text-[#3A3632] mt-2">Boost a kit with extra supplies</h3>
              <p className="text-[#6B655F] mt-2">Add a full set of additional supplies to any kit — from $17.99.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {addons.map((a) => {
                const added = items.some((i) => i.id === a.id);
                return (
                  <div key={a.id} data-testid={`gia-addon-${a.slug}`} className="bg-white border border-[#E5DFD5] rounded-2xl p-5 flex gap-4 items-center">
                    <button type="button" onClick={() => setLightbox({ src: a.image, name: a.name.replace("Hope in Action · ", "") })}
                      data-testid={`gia-addon-img-${a.slug}`} className="shrink-0 relative group rounded-xl overflow-hidden border border-[#E5DFD5]" aria-label="View larger image">
                      <img src={a.image} alt={a.name} className="w-24 h-24 object-cover group-hover:opacity-90 transition-opacity" loading="lazy" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/25 transition-colors">
                        <Search className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </span>
                    </button>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading text-lg text-[#3A3632] leading-tight">{a.name.replace("Hope in Action · ", "")}</h4>
                      <p className="text-xs text-[#6B655F] mt-1">{a.short_desc}</p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="font-heading text-xl text-[#8E9A86]">{money(a.price)}</span>
                        <button onClick={() => addAddon(a)} disabled={added} data-testid={`gia-addon-add-${a.slug}`}
                          className="rounded-full px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 bg-[#C4A4A4] text-white hover:bg-[#B09090] transition-colors disabled:opacity-60">
                          {added ? <><Check className="h-3.5 w-3.5" /> Added</> : <><Plus className="h-3.5 w-3.5" /> Add {money(a.price)}</>}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Dignity band */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-2 text-center" data-testid="gia-showcase">
        <div className="mb-8">
          <span className="gh-overline">Restoring dignity</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#3A3632] mt-3">Meeting real needs with love</h2>
          <p className="text-[#6B655F] mt-3">Every kit is a reminder that someone sees them, and cares.</p>
        </div>
        <figure className="rounded-2xl overflow-hidden border border-[#D9CBA0]/50 shadow-sm bg-[#FBF7F0] max-w-2xl mx-auto">
          <img src={showcaseImg} alt="A comforting moment of two people holding hands." className="w-full h-auto" loading="lazy" />
        </figure>
      </section>

      {/* Dignity affirmations */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-14">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="rounded-2xl p-8 text-center bg-[#C4A4A4]/10 border border-[#C4A4A4]/30">
            <p className="font-hand text-2xl text-[#C4A4A4]">You are seen. You are valued. You are not alone.</p>
          </div>
          <div className="rounded-2xl p-8 text-center bg-[#8E9A86]/10 border border-[#8E9A86]/30">
            <p className="font-hand text-2xl text-[#8E9A86]">You matter. You are valued. You have purpose.</p>
          </div>
        </div>
      </section>

      {/* Ways you can help */}
      <section className="bg-[#3A3632] py-20" data-testid="gia-ways-to-help">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-10">
            <Sparkles className="h-6 w-6 text-[#D9CBA0] mx-auto" />
            <h2 className="font-heading text-3xl sm:text-4xl text-[#FAF7F2] mt-4">Ways you can help</h2>
            <p className="text-[#D9CBA0]/90 mt-3">Together, we can bring grace, hope, and dignity to our community.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WAYS.map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} data-testid={`gia-way-${w.title.split(" ")[0].toLowerCase()}`} className="bg-[#FAF7F2] rounded-2xl p-6 flex flex-col">
                  <Icon className="h-7 w-7 text-[#8E9A86]" />
                  <h3 className="font-heading text-xl text-[#3A3632] mt-3">{w.title}</h3>
                  <p className="text-sm text-[#6B655F] mt-1.5 leading-relaxed flex-1">{w.desc}</p>
                  {w.action && <button onClick={w.action} data-testid={`gia-way-cta-${w.title.split(" ")[0].toLowerCase()}`} className="mt-4 text-sm text-[#8E9A86] font-medium gh-link-underline inline-flex items-center gap-1 self-start">{w.cta} <ArrowRight className="h-3.5 w-3.5" /></button>}
                  {w.to && <Link to={w.to} data-testid={`gia-way-cta-${w.title.split(" ")[0].toLowerCase()}`} className="mt-4 text-sm text-[#8E9A86] font-medium gh-link-underline inline-flex items-center gap-1 self-start">{w.cta} <ArrowRight className="h-3.5 w-3.5" /></Link>}
                  {w.info && <span className="mt-4 text-xs text-[#8A8175] italic">Reach out via Contact to arrange.</span>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" data-testid="gia-lightbox"
          onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} data-testid="gia-lightbox-close"
            className="absolute top-5 right-5 text-white/80 hover:text-white" aria-label="Close">
            <X className="h-8 w-8" />
          </button>
          <figure className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.name} className="w-full h-auto rounded-2xl shadow-2xl" />
            <figcaption className="text-center text-[#F2EFEB] mt-4 font-heading text-xl">{lightbox.name}</figcaption>
          </figure>
        </div>
      )}
    </div>
  );
}
